import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { formatMoscowDateTime } from '@/utils/format'
import { roleLabelForTemplate } from '@/utils/notification-ui'
import type { NotificationOutboxRow } from '@/api/notifications'
import { USE_MOCK } from '@/config'
import i18n from '@/utils/i18n'

function sourceLabel(s: string): string {
  if (s === 'trigger') return i18n.t('notif.source.trigger')
  if (s === 'broadcast') return i18n.t('notif.source.broadcast')
  if (s === 'manual_resend') return i18n.t('notif.source.manual_resend')
  return s
}

export function NotificationOutboxDrawer({
  open,
  row,
  onClose,
  onResend,
  resendPending,
}: {
  open: boolean
  row: NotificationOutboxRow | null
  onClose: () => void
  onResend: (outboxId: string) => void
  resendPending: boolean
}) {
  const { t } = useTranslation()
  if (!open || !row) return null

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-[color:var(--stryzh-overlay-scrim-modal)]"
      role="presentation"
      onClick={onClose}
    >
      <aside
        className="flex h-full w-[var(--stryzh-layout-entity-drawer-width)] max-w-full flex-col overflow-hidden border-l border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] shadow-[var(--stryzh-shadow-glow-strong)]"
        role="dialog"
        aria-labelledby="outbox-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[color:var(--stryzh-color-border-default)] px-[var(--stryzh-space-6)] py-[var(--stryzh-space-5)]">
          <h2
            id="outbox-drawer-title"
            className="font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-lg)] font-semibold tracking-[var(--stryzh-letter-spacing-space-grotesk)] text-[color:var(--stryzh-color-text-primary)]"
          >
            {t('notif.outboxDrawer.title')}
          </h2>
          <Button variant="ghost" onClick={onClose} aria-label={t('notif.outboxDrawer.close')} className="stryzh-btn--close-icon">
            ✕
          </Button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto px-[var(--stryzh-space-6)] py-[var(--stryzh-space-5)] text-sm">
          <dl className="space-y-3">
            <div>
              <dt className="text-xs text-[color:var(--stryzh-color-text-secondary)]">{t('notif.outboxDrawer.id')}</dt>
              <dd className="font-mono text-xs break-all">{row.id}</dd>
            </div>
            <div>
              <dt className="text-xs text-[color:var(--stryzh-color-text-secondary)]">{t('notif.outboxDrawer.sent')}</dt>
              <dd>{row.sent_at ? formatMoscowDateTime(row.sent_at) : '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-[color:var(--stryzh-color-text-secondary)]">{t('notif.outboxDrawer.created')}</dt>
              <dd>{formatMoscowDateTime(row.created_at)}</dd>
            </div>
            <div>
              <dt className="text-xs text-[color:var(--stryzh-color-text-secondary)]">{t('notif.outboxDrawer.event')}</dt>
              <dd className="font-mono text-xs">{row.event_type}</dd>
            </div>
            <div>
              <dt className="text-xs text-[color:var(--stryzh-color-text-secondary)]">{t('notif.outboxDrawer.source')}</dt>
              <dd>{sourceLabel(row.source)}</dd>
            </div>
            <div>
              <dt className="text-xs text-[color:var(--stryzh-color-text-secondary)]">{t('notif.outboxDrawer.status')}</dt>
              <dd className="font-mono text-xs">{row.status}</dd>
            </div>
            {row.broadcast_id ? (
              <div>
                <dt className="text-xs text-[color:var(--stryzh-color-text-secondary)]">broadcast_id</dt>
                <dd className="font-mono text-xs break-all">{row.broadcast_id}</dd>
              </div>
            ) : null}
            {row.fcm_message_id ? (
              <div>
                <dt className="text-xs text-[color:var(--stryzh-color-text-secondary)]">FCM message id</dt>
                <dd className="font-mono text-xs break-all">{row.fcm_message_id}</dd>
              </div>
            ) : null}
            <div>
              <dt className="text-xs text-[color:var(--stryzh-color-text-secondary)]">{t('notif.outboxDrawer.recipient')}</dt>
              <dd>
                <div>{row.recipient_full_name ?? '—'}</div>
                {row.recipient_role ? (
                  <div className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
                    {roleLabelForTemplate(row.recipient_role)}
                  </div>
                ) : null}
                <div className="mt-1 font-mono text-xs text-[color:var(--stryzh-color-text-secondary)]">{row.user_id}</div>
                <Link
                  className="mt-2 inline-block text-xs text-[color:var(--stryzh-color-accent-orange)] hover:underline"
                  to={`/users/${row.user_id}`}
                >
                  {t('notif.outboxDrawer.openProfile')}
                </Link>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[color:var(--stryzh-color-text-secondary)]">{t('notif.outboxDrawer.order')}</dt>
              <dd>
                {row.order_id ? (
                  <Link
                    className="text-[color:var(--stryzh-color-accent-orange)] hover:underline"
                    to={`/orders/${row.order_id}`}
                  >
                    {row.order_label ?? row.order_id.slice(0, 8)}
                  </Link>
                ) : (
                  '—'
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[color:var(--stryzh-color-text-secondary)]">{t('notif.outboxDrawer.delivered')}</dt>
              <dd>{row.delivered_at ? formatMoscowDateTime(row.delivered_at) : '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-[color:var(--stryzh-color-text-secondary)]">{t('notif.outboxDrawer.opened')}</dt>
              <dd>{row.opened_at ? formatMoscowDateTime(row.opened_at) : '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-[color:var(--stryzh-color-text-secondary)]">{t('notif.outboxDrawer.error')}</dt>
              <dd className="text-[color:var(--stryzh-color-status-error)]">{row.error ?? '—'}</dd>
            </div>
          </dl>
        </div>
        <div className="border-t border-[color:var(--stryzh-color-border-default)] px-[var(--stryzh-space-6)] py-[var(--stryzh-space-5)]">
          <Button
            type="button"
            variant="primary"
            className="w-full"
            disabled={resendPending || USE_MOCK}
            onClick={() => onResend(row.id)}
          >
            {t('notif.outboxDrawer.resend')}
          </Button>
        </div>
      </aside>
    </div>
  )
}
